/**
 * Mock response shape for Contract Element Summary (matches stored procedure output).
 * For richer demos, add more named exports here and import the chosen one in service.js (mock branch).
 *
 * kpi_type_name and kpi_desc head each KPI group in the report.
 *
 * The proc returns one row per period version of an element's value rather than one row
 * per element, so these rows are expanded the same way: oldest to newest, each carrying
 * the version before it. The report collapses them back to a single element line and
 * hangs the history off its comparison popover.
 */

/** kpi_type_master.kpi_type_name and kpi_master.kpi_desc, which the proc repeats on every element row. */
const KPI_META = {
  Muse: { kpi_type_name: "Transactional Payment", kpi_desc: "FMG - Muse" },
  FTE: { kpi_type_name: "Admin Payment", kpi_desc: "System" },
  "Draw Pay Calculations": {
    kpi_type_name: "Bonus Payment",
    kpi_desc: "FMG - Draw pay thresholds, multipliers and tier rates",
  },
};

/**
 * Periods an element's versions are spread across, oldest to newest. An element holding
 * N values takes the last N of these, so its newest value always lands on OCT-2025.
 */
const PERIODS = [
  { eff_start_date: "2024-10-01 00:00:00", period_name: "OCT-2024" },
  { eff_start_date: "2025-01-01 00:00:00", period_name: "JAN-2025" },
  { eff_start_date: "2025-10-01 00:00:00", period_name: "OCT-2025" },
];

/**
 * Values an element held before the one on its base row, oldest first. Elements absent
 * from this map have only ever held one value, so the proc returns a single row for them
 * and the report renders them without a delta badge or comparison popover — both states
 * are represented here on purpose.
 */
const ELEMENT_VERSION_HISTORY = {
  "Current Clinical FTE": [0.9],
  "65th percentile wRVUs": [10900.0],
  "90th percentile wRVUs": [15000.0, 15800.0],
  "Median Compensation per wRVU": [70.07],
  "Draw Pay Tier 0 Rate": [68.13],
  "Draw Pay Tier 1 Rate": [70.07],
  "Draw Pay Tier 2 Rate": [73.57],
  "Draw Pay Tier 3 Rate": [77.08],
};

/**
 * Expands one element into the row-per-version resultset the proc returns. The element
 * version column stays at 1 throughout because contract_element_report_mvw versions a
 * value by its effective date, not by that column.
 */
function expandElementVersions(row) {
  const values = [
    ...(ELEMENT_VERSION_HISTORY[row.contract_element_name] || []),
    row.value_amt,
  ];
  const periods = PERIODS.slice(Math.max(0, PERIODS.length - values.length));
  const kpiMeta = KPI_META[row.kpi_name] || {
    kpi_type_name: null,
    kpi_desc: null,
  };

  return values.map((value, index) => {
    const priorValue = index === 0 ? null : values[index - 1];
    const priorPeriod = index === 0 ? null : periods[index - 1];

    return {
      ...row,
      ...kpiMeta,
      value_amt: value,
      element_version: 1,
      eff_start_date: periods[index].eff_start_date,
      period_name: periods[index].period_name,
      prior_value_amt: priorValue,
      prior_element_version: priorValue === null ? null : 1,
      prior_eff_start_date: priorPeriod ? priorPeriod.eff_start_date : null,
      prior_period_name: priorPeriod ? priorPeriod.period_name : null,
      value_delta: priorValue === null ? null : value - priorValue,
      version_seq: index + 1,
      version_count: values.length,
      is_latest_version: index === values.length - 1 ? 1 : 0,
    };
  });
}

function withKpiAndVersionColumns(rows) {
  return rows.flatMap(expandElementVersions);
}

export const MOCK_CONTRACT_ELEMENT_REPORT = {
  out_parameters: {
    // Derived below: expanding each element into one row per version makes the count
    // depend on ELEMENT_VERSION_HISTORY rather than on the base rows alone.
    out_total_rows: 0,
    out_ret_code: 0,
    out_error_msg: "",
  },
  row_result: withKpiAndVersionColumns([
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Muse",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Muse Multiplier",
      contr_el_source_name: "FMG Ref",
      entity_ref_level: "Comp Plan",
      report_order: 1,
      value_amt: 0.17,
      system_unit_name: "multiplier",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "FTE",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Current Total FTE",
      contr_el_source_name: "FMG Position Attributes",
      entity_ref_level: "Position",
      report_order: 2,
      value_amt: 1.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.0",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "FTE",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Current Clinical FTE",
      contr_el_source_name: "FMG Position Attributes",
      entity_ref_level: "Position",
      report_order: 3,
      value_amt: 1.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.0",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "50th percentile wRVUs",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 4,
      value_amt: 9135.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "65th percentile wRVUs",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 5,
      value_amt: 11173.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "90th percentile wRVUs",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 6,
      value_amt: 16551.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Multiplier",
      contr_el_source_name: "FMG Ref",
      entity_ref_level: "Comp Plan",
      report_order: 7,
      value_amt: 0.85,
      system_unit_name: "multiplier",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Median Compensation per wRVU",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 8,
      value_amt: 69.09,
      system_unit_name: "currency",
      unit_type_report_format: "$#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 0 wRVU Threshold",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 9,
      value_amt: 9135.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 0 Rate",
      contr_el_source_name: "FMG Benchmark Rate",
      entity_ref_level: "Specialty",
      report_order: 10,
      value_amt: 67.18,
      system_unit_name: "currency",
      unit_type_report_format: "$#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 1 wRVU Threshold",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 11,
      value_amt: 9135.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 1 Rate",
      contr_el_source_name: "FMG Benchmark Rate",
      entity_ref_level: "Specialty",
      report_order: 12,
      value_amt: 69.09,
      system_unit_name: "currency",
      unit_type_report_format: "$#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 2 wRVU Threshold",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 13,
      value_amt: 11173.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 2 Multiplier",
      contr_el_source_name: "FMG Ref",
      entity_ref_level: "Comp Plan",
      report_order: 14,
      value_amt: 1.05,
      system_unit_name: "multiplier",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 2 Rate",
      contr_el_source_name: "FMG Benchmark Rate",
      entity_ref_level: "Specialty",
      report_order: 15,
      value_amt: 72.54,
      system_unit_name: "currency",
      unit_type_report_format: "$#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 3 wRVU Threshold",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 16,
      value_amt: 16551.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 3 Multiplier",
      contr_el_source_name: "FMG Ref",
      entity_ref_level: "Comp Plan",
      report_order: 17,
      value_amt: 1.1,
      system_unit_name: "multiplier",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 3 Rate",
      contr_el_source_name: "FMG Benchmark Rate",
      entity_ref_level: "Specialty",
      report_order: 18,
      value_amt: 76.0,
      system_unit_name: "currency",
      unit_type_report_format: "$#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 4 Threshold",
      contr_el_source_name: "FMG Benchmark RVU",
      entity_ref_level: "Specialty",
      report_order: 19,
      value_amt: 999999.0,
      system_unit_name: "number",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 4 Multiplier",
      contr_el_source_name: "FMG Ref",
      entity_ref_level: "Comp Plan",
      report_order: 20,
      value_amt: 1.0,
      system_unit_name: "multiplier",
      unit_type_report_format: "#,##0.00",
    },
    {
      comp_plan_name: "FMG - Draw Pay",
      kpi_name: "Draw Pay Calculations",
      position_name: "Eagan, Roger 155499",
      contract_element_name: "Draw Pay Tier 4 Rate",
      contr_el_source_name: "FMG Benchmark Rate",
      entity_ref_level: "Specialty",
      report_order: 21,
      value_amt: 69.09,
      system_unit_name: "currency",
      unit_type_report_format: "$#,##0.00",
    },
  ]),
};

MOCK_CONTRACT_ELEMENT_REPORT.out_parameters.out_total_rows =
  MOCK_CONTRACT_ELEMENT_REPORT.row_result.length;

const roundAmt = (v) => Math.round(Number(v) * 100) / 100;

const scaleAmt = (value, factor) =>
  typeof value === "number" && !Number.isNaN(value)
    ? roundAmt(value * factor)
    : value;

function mapRowsWithPlanPosition(rows, compPlanName, positionName, factor) {
  return rows.map((r) => ({
    ...r,
    comp_plan_name: compPlanName,
    position_name: positionName,
    value_amt: scaleAmt(r.value_amt, factor),
    prior_value_amt: scaleAmt(r.prior_value_amt, factor),
    value_delta: scaleAmt(r.value_delta, factor),
  }));
}

/** FMG - Draw Pay + Mayer: same rows as baseline Eagan, values +15% (within 10–20% range). */
export const MOCK_FMG_MAYER_REPORT = {
  out_parameters: {
    ...MOCK_CONTRACT_ELEMENT_REPORT.out_parameters,
    out_total_rows: MOCK_CONTRACT_ELEMENT_REPORT.row_result.length,
  },
  row_result: mapRowsWithPlanPosition(
    MOCK_CONTRACT_ELEMENT_REPORT.row_result,
    "FMG - Draw Pay",
    "Mayer, Alissa 661510",
    1.15
  ),
};

/** FTE + Payne: same structure, values −10%. */
export const MOCK_FTE_PAYNE_REPORT = {
  out_parameters: {
    ...MOCK_CONTRACT_ELEMENT_REPORT.out_parameters,
    out_total_rows: MOCK_CONTRACT_ELEMENT_REPORT.row_result.length,
  },
  row_result: mapRowsWithPlanPosition(
    MOCK_CONTRACT_ELEMENT_REPORT.row_result,
    "FTE",
    "Payne, Walter 350607",
    0.9
  ),
};

export const MOCK_ERROR_RESPONSE = {
  out_parameters: {
    out_total_rows: 0,
    out_ret_code: -1,
    out_error_msg: "Invalid parameters provided",
  },
  row_result: [],
};

/** Comp plan filter mock (matches sp_select_comp_plan_summary_filter_comp_plan result columns). */
export const MOCK_COMP_PLAN_FILTER = [
  { comp_plan_mstr_key: 2, comp_plan_name: "FMG - Draw Pay" },
  { comp_plan_mstr_key: 26, comp_plan_name: "FTE" },
];

/** Position filter mock keyed by comp plan key (matches sp_select_comp_plan_summary_filter_position result columns). */
export const MOCK_POSITION_FILTER = {
  2: [
    { org_position_key: 2270, position_name: "Eagan, Roger 155499" },
    { org_position_key: 20724, position_name: "Mayer, Alissa 661510" },
  ],
  26: [{ org_position_key: 20927, position_name: "Payne, Walter 350607" }],
};

/**
 * Contractual Details mock keyed by position key, matching the
 * sp_select_provider_position_contract_details result columns: one row per contract
 * tier, with the contract level columns repeated on every row of that contract. The
 * proc keeps the source column spellings (employement_type is aliased back to
 * employment_type, min_thresold / max_thresold / thresold_unit_type are not).
 * contract_rate is the contract's headline rate (MAX across its tiers) and tier_rate
 * the amount for that tier, so a step contract keeps both.
 *
 * Providers routinely hold more than one contract at a time, so every mocked position
 * carries at least two — one stepped and one flat — to exercise the section's per
 * contract collapsing.
 */
const MOCK_CALL_OBGYN_CONTRACT = {
  org_position_key: 2270,
  provider_mstr_key: 8841,
  contract_mstr_key: 4411,
  contract_version: 1,
  division_mstr_key: 12,
  division_name: "VMFH Division",
  location_mstr_key: 340,
  location_name: "CHI VMFH - St. Anne Hospital SANH",
  contract_type_mstr_key: 5,
  contract_type: "On Call",
  contract_name_mstr_key: 118,
  contract_name: "CALL - OB/GYN (hourly)",
  specialty_mstr_key: 27,
  specialty_name: "OB/GYN",
  contract_start_date: "2025-10-01",
  contract_end_date: "2027-09-30",
  employment_type: "Employed",
  shift: 24,
  shift_type_mstr_key: 3,
  shift_type_name: "Night, Weekend",
  period_type: "monthly",
  rate_type_mstr_key: 2,
  rate_type: "step",
  rate_type_display_name: "Step",
  contract_rate: "990.0000",
  contract_rate_unit_type: "daily",
  tier_count: 2,
  approver1_name: "Mobile Approver",
  approver1_email: "mobile.approver@procareportal.com",
  approver2_name: "PS Tester",
  approver2_email: "ps.tester@procareportal.com",
  approver3_name: "Comp Admin",
  approver3_email: null,
};

const MOCK_MED_DIR_SLEEP_CONTRACT = {
  org_position_key: 2270,
  provider_mstr_key: 8841,
  contract_mstr_key: 4682,
  contract_version: 2,
  division_mstr_key: 12,
  division_name: "VMFH Division",
  location_mstr_key: 340,
  location_name: "CHI VMFH - St. Anne Hospital SANH",
  contract_type_mstr_key: 3,
  contract_type: "Medical Directorship",
  contract_name_mstr_key: 209,
  contract_name: "Med Dir - Sleep Medicine",
  specialty_mstr_key: 27,
  specialty_name: "OB/GYN",
  contract_start_date: "2025-07-01",
  contract_end_date: "2027-06-30",
  employment_type: "Employed",
  shift: null,
  shift_type_mstr_key: null,
  shift_type_name: null,
  period_type: "monthly",
  rate_type_mstr_key: 2,
  rate_type: "step",
  rate_type_display_name: "Step",
  contract_rate: "185.0000",
  contract_rate_unit_type: "hourly",
  tier_count: 2,
  approver1_name: "Mobile Approver",
  approver1_email: "mobile.approver@procareportal.com",
  approver2_name: "PS Tester",
  approver2_email: "ps.tester@procareportal.com",
  approver3_name: "Comp Admin",
  approver3_email: null,
};

const MOCK_ADMIN_MED_DIRECTOR_CONTRACT = {
  org_position_key: 20724,
  provider_mstr_key: 9127,
  contract_mstr_key: 4507,
  contract_version: 1,
  division_mstr_key: 8,
  division_name: "FMG Division",
  location_mstr_key: 211,
  location_name: "CHI Franciscan - St. Joseph Medical Center",
  contract_type_mstr_key: 2,
  contract_type: "Administrative",
  contract_name_mstr_key: 74,
  contract_name: "ADMIN - Medical Director",
  specialty_mstr_key: 14,
  specialty_name: "Family Medicine",
  contract_start_date: "2026-01-01",
  contract_end_date: "2026-12-31",
  employment_type: "Employed",
  shift: 8,
  shift_type_mstr_key: 1,
  shift_type_name: "Day",
  period_type: "monthly",
  rate_type_mstr_key: 1,
  rate_type: "flat",
  rate_type_display_name: "Flat",
  contract_rate: "155.0000",
  contract_rate_unit_type: "hourly",
  tier_count: 1,
  approver1_name: "Mobile Approver",
  approver1_email: "mobile.approver@procareportal.com",
  approver2_name: "Comp Admin",
  approver2_email: null,
  approver3_name: null,
  approver3_email: null,
};

const MOCK_CALL_HOSPITALIST_CONTRACT = {
  org_position_key: 20724,
  provider_mstr_key: 9127,
  contract_mstr_key: 4611,
  contract_version: 1,
  division_mstr_key: 8,
  division_name: "FMG Division",
  location_mstr_key: 211,
  location_name: "CHI Franciscan - St. Joseph Medical Center",
  contract_type_mstr_key: 5,
  contract_type: "On Call",
  contract_name_mstr_key: 131,
  contract_name: "CALL - Hospitalist (daily)",
  specialty_mstr_key: 14,
  specialty_name: "Family Medicine",
  contract_start_date: "2025-04-01",
  contract_end_date: "2026-03-31",
  employment_type: "Employed",
  shift: 12,
  shift_type_mstr_key: 2,
  shift_type_name: "Night, Holiday",
  period_type: "monthly",
  rate_type_mstr_key: 1,
  rate_type: "flat",
  rate_type_display_name: "Flat",
  contract_rate: "725.0000",
  contract_rate_unit_type: "daily",
  tier_count: 1,
  approver1_name: "PS Tester",
  approver1_email: "ps.tester@procareportal.com",
  approver2_name: null,
  approver2_email: null,
  approver3_name: null,
  approver3_email: null,
};

const MOCK_PLASTICS_TRAUMA_CALL_CONTRACT = {
  org_position_key: 20927,
  provider_mstr_key: 9344,
  contract_mstr_key: 4733,
  contract_version: 1,
  division_mstr_key: 12,
  division_name: "VMFH Division",
  location_mstr_key: 355,
  location_name: "CHI VMFH - St. Joseph Medical Center Gig Harbor",
  contract_type_mstr_key: 5,
  contract_type: "On Call",
  contract_name_mstr_key: 164,
  contract_name: "CALL - Plastics Trauma (daily)",
  specialty_mstr_key: 41,
  specialty_name: "Plastic Surgery",
  contract_start_date: "2025-01-01",
  contract_end_date: "2026-12-31",
  employment_type: "Employed",
  shift: 24,
  shift_type_mstr_key: 3,
  shift_type_name: "Weekend",
  period_type: "monthly",
  rate_type_mstr_key: 2,
  rate_type: "step",
  rate_type_display_name: "Step",
  contract_rate: "1250.0000",
  contract_rate_unit_type: "daily",
  tier_count: 2,
  approver1_name: "Comp Admin",
  approver1_email: null,
  approver2_name: null,
  approver2_email: null,
  approver3_name: null,
  approver3_email: null,
};

const MOCK_TELEHEALTH_COVERAGE_CONTRACT = {
  org_position_key: 20927,
  provider_mstr_key: 9344,
  contract_mstr_key: 4790,
  contract_version: 1,
  division_mstr_key: 12,
  division_name: "VMFH Division",
  location_mstr_key: 355,
  location_name: "CHI VMFH - St. Joseph Medical Center Gig Harbor",
  contract_type_mstr_key: 7,
  contract_type: "Telehealth",
  contract_name_mstr_key: 178,
  contract_name: "TELE - Virtual Clinic Coverage",
  specialty_mstr_key: 41,
  specialty_name: "Plastic Surgery",
  contract_start_date: "2026-01-01",
  contract_end_date: "2026-12-31",
  employment_type: "Employed",
  shift: 4,
  shift_type_mstr_key: 1,
  shift_type_name: "Day",
  period_type: "weekly",
  rate_type_mstr_key: 1,
  rate_type: "flat",
  rate_type_display_name: "Flat",
  contract_rate: "210.0000",
  contract_rate_unit_type: "hourly",
  tier_count: 1,
  approver1_name: "Mobile Approver",
  approver1_email: "mobile.approver@procareportal.com",
  approver2_name: "Comp Admin",
  approver2_email: null,
  approver3_name: null,
  approver3_email: null,
};

const contractDetailsResult = (rows) => ({
  out_parameters: {
    out_total_rows: rows.length,
    out_ret_code: 0,
    out_error_msg: "",
  },
  row_result: rows,
});

export const MOCK_POSITION_CONTRACT_DETAILS = {
  2270: contractDetailsResult([
    {
      ...MOCK_CALL_OBGYN_CONTRACT,
      contract_tier: 1,
      min_thresold: "0.0000",
      max_thresold: "96.0000",
      thresold_unit_type: "hours",
      tier_rate: "0.0000",
      tier_rate_unit_type: "daily",
    },
    {
      ...MOCK_CALL_OBGYN_CONTRACT,
      contract_tier: 2,
      min_thresold: "96.0000",
      max_thresold: "744.0000",
      thresold_unit_type: "hours",
      tier_rate: "990.0000",
      tier_rate_unit_type: "daily",
    },
    {
      ...MOCK_MED_DIR_SLEEP_CONTRACT,
      contract_tier: 1,
      min_thresold: "0.0000",
      max_thresold: "20.0000",
      thresold_unit_type: "hours",
      tier_rate: "185.0000",
      tier_rate_unit_type: "hourly",
    },
    {
      ...MOCK_MED_DIR_SLEEP_CONTRACT,
      contract_tier: 2,
      min_thresold: "20.0000",
      max_thresold: "40.0000",
      thresold_unit_type: "hours",
      tier_rate: "0.0000",
      tier_rate_unit_type: "hourly",
    },
  ]),
  20724: contractDetailsResult([
    {
      ...MOCK_ADMIN_MED_DIRECTOR_CONTRACT,
      contract_tier: 1,
      min_thresold: "0.0000",
      max_thresold: null,
      thresold_unit_type: "hours",
      tier_rate: "155.0000",
      tier_rate_unit_type: "hourly",
    },
    {
      ...MOCK_CALL_HOSPITALIST_CONTRACT,
      contract_tier: 1,
      min_thresold: "0.0000",
      max_thresold: "365.0000",
      thresold_unit_type: "days",
      tier_rate: "725.0000",
      tier_rate_unit_type: "daily",
    },
  ]),
  20927: contractDetailsResult([
    {
      ...MOCK_PLASTICS_TRAUMA_CALL_CONTRACT,
      contract_tier: 1,
      min_thresold: "0.0000",
      max_thresold: "48.0000",
      thresold_unit_type: "hours",
      tier_rate: "0.0000",
      tier_rate_unit_type: "daily",
    },
    {
      ...MOCK_PLASTICS_TRAUMA_CALL_CONTRACT,
      contract_tier: 2,
      min_thresold: "48.0000",
      max_thresold: "744.0000",
      thresold_unit_type: "hours",
      tier_rate: "1250.0000",
      tier_rate_unit_type: "daily",
    },
    {
      ...MOCK_TELEHEALTH_COVERAGE_CONTRACT,
      contract_tier: 1,
      min_thresold: "0.0000",
      max_thresold: null,
      thresold_unit_type: "hours",
      tier_rate: "210.0000",
      tier_rate_unit_type: "hourly",
    },
  ]),
};

/**
 * Comp attribute field metadata mock (matches SP_SELECT_CATEGORY_FIELD result columns
 * for category_code = 'comp_attr'). Mirrors the labels and order the Provider Profile
 * screen renders.
 */
export const MOCK_PROVIDER_PROFILE_FIELDS = [
  { cat_fdm_order: 1, filter_label: "Total FTE Practice", filter_column_name: "Total_FTE_Practice" },
  { cat_fdm_order: 2, filter_label: "UTILIZATION ADMIN", filter_column_name: "b.utilization_admin" },
  { cat_fdm_order: 3, filter_label: "UTILIZATION CLINICAL", filter_column_name: "b.utilization_clinical" },
  { cat_fdm_order: 4, filter_label: "EMPLOYEE ID", filter_column_name: "employee_id" },
  { cat_fdm_order: 5, filter_label: "Home Group", filter_column_name: "Home_Group" },
  { cat_fdm_order: 6, filter_label: "Call FTE", filter_column_name: "Call_FTE" },
  { cat_fdm_order: 7, filter_label: "Specialty CS", filter_column_name: "Specialty_CS" },
  { cat_fdm_order: 8, filter_label: "UTILIZATION TOTAL", filter_column_name: "b.utilization_total" },
];

/**
 * Provider profile value mock keyed by position key (matches sp_select_provider_position_profile
 * row_result[0]). Values mirror the shape and length of real tenant data: Home Group holds short
 * site names, while Specialty CS is the field that runs long. The first position carries the
 * longest Specialty CS on file so the widest case is visible by default.
 */
export const MOCK_PROVIDER_PROFILE = {
  2270: {
    Total_FTE_Practice: "1.00",
    utilization_admin: "0.10",
    utilization_clinical: "0.90",
    employee_id: "155499",
    Home_Group: "Federal Way",
    Call_FTE: "0.25",
    Specialty_CS: "Pulmonary Medicine: General and Critical Care",
    utilization_total: "1.00",
  },
  20724: {
    Total_FTE_Practice: "0.80",
    utilization_admin: "0.20",
    utilization_clinical: "0.60",
    employee_id: "661510",
    Home_Group: "Hospitalist",
    Call_FTE: "0.15",
    Specialty_CS: "Cardiology: Invasive-Interventional",
    utilization_total: "0.80",
  },
  20927: {
    Total_FTE_Practice: "0.90",
    utilization_admin: "",
    utilization_clinical: "0.90",
    employee_id: "350607",
    Home_Group: "Gig Harbor",
    Call_FTE: "0.50",
    Specialty_CS: "Surgery: Plastic and Reconstruction",
    utilization_total: "0.90",
  },
};
